/**
 * Global Use Disclaimer Banner
 *
 * Renders on the dashboard above the chat area. Constantly reminds users that:
 *   - AI output may be wrong
 *   - It is NOT professional advice (legal/medical/financial/safety)
 *   - They must verify before acting
 *   - Use is governed by Terms / Disclaimer (linked)
 *
 * Liability-protection role: gives clear, conspicuous notice of all material
 * limitations BEFORE every interaction. Combined with the Terms of Service
 * limitation-of-liability clauses, this is the standard SaaS "constructive
 * notice" pattern (Stripe / OpenAI / Anthropic all do this).
 *
 * Dismissible per browser (localStorage). Reappears if the user clears storage
 * or logs in from a new device.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './UseDisclaimerBanner.css';

const STORAGE_KEY = 'sb_use_disclaimer_dismissed_v1';

export const UseDisclaimerBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(localStorage.getItem(STORAGE_KEY) !== '1');
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  return (
    <div className="use-disclaimer" role="note" data-testid="use-disclaimer-banner">
      <span className="ud-icon" aria-hidden>⚠️</span>
      <p className="ud-text">
        <strong>{t('disclaimer.banner_title')}</strong>{' '}
        {t('disclaimer.banner_body')}{' '}
        <a href="/disclaimer" className="ud-link" data-testid="use-disclaimer-link">
          {t('disclaimer.full_disclaimer')}
        </a>
      </p>
      <button
        className="ud-dismiss"
        onClick={dismiss}
        aria-label={t('common.close')}
        data-testid="use-disclaimer-dismiss"
      >
        ✕
      </button>
    </div>
  );
};

export default UseDisclaimerBanner;
